/* SeatFit core, shared by the picker drawer and the product "Customize Your Vehicle" block. Plain ES2019,
 * loaded `defer` first by snippets/seatfit-config.liquid; seatfit-core-resolve.js (resolve),
 * seatfit-core-url-params.js (readParams, writeParams), seatfit-core-cover-mat-rules.js and seatfit-core-rules.js
 * (stepsFor, vehicleLabel, cartProperties) follow, and the last emits 'seatfit:ready' once the whole API is there.
 * One code base serves every product kind (seat covers, car covers, floor mats); each kind has its own data files. */
(function () {
  'use strict';
  // The snippet may be rendered by several sections; a second copy must not reset the caches.
  if (window.SeatFit) return;

  var SETS = ['front', 'full', 'rear', 'third'];
  // Seat Set option values on the product; the URL only carries the short code.
  var SET_VALUES = { front: 'Front Seats', full: 'Full Set', rear: 'Rear Seats', third: 'Third Row' };
  // Products sold with this vehicle data: make files <prefix><make>.json next to <prefix>index.json. The Liquid
  // config prints the same table with versioned asset URLs; these defaults serve a page without it.
  var KINDS = {
    seat: { prefix: 'seatfitc-fitment-', productUrl: '/products/custom-fit-leather-seat-covers', label: 'Seat Covers' },
    cover: { prefix: 'coverfit-fitment-', productUrl: '/products/custom-fit-car-covers', label: 'Car Covers' },
    mat: { prefix: 'matfit-fitment-', productUrl: '/products/custom-fit-floor-mats', label: 'Floor Mats' }
  };
  // document.currentScript is only set while this file runs, so capture it now for the fallback.
  var ownSrc = (document.currentScript && document.currentScript.src) || '';
  var cfg = null;
  var jsonCache = {};

  function own(obj, key) { return !!obj && Object.prototype.hasOwnProperty.call(obj, key); }

  // Must stay identical to slugify() in scripts/seatfit_fitment_lib.py (builds the data keys) and in
  // snippets/seatfit-head-prefetch.liquid; any drift would make a valid ad-link vehicle look unsupported.
  function slugify(str) {
    return String(str == null ? '' : str).toLowerCase().replace(/&/g, 'and').replace(/['’.]/g, '')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function toYear(v) {
    var s = String(v == null ? '' : v).trim();
    return /^\d{4}$/.test(s) ? Number(s) : null;
  }

  // Full Set asks front and rear subtypes but the URL has one `seat` param: one slug per asked row joined
  // with '_' (slugify never makes one). An open row keeps its empty slot ("_captain-seats" = front open,
  // rear chosen) so a choice never shifts onto another row.
  function seatSlugs(v) {
    var list = (Array.isArray(v) ? v : String(v == null ? '' : v).split('_')).map(slugify);
    while (list.length && !list[list.length - 1]) list.pop();
    return list.join('_');
  }

  function config() {
    if (cfg) return cfg;
    var el = document.getElementById('seatfit-config');
    var c = {};
    if (el) {
      try { c = JSON.parse(el.textContent || '{}') || {}; } catch (e) { console.warn('[SeatFit] bad #seatfit-config JSON', e); }
    }
    // Data files sit next to this script, so a missing config tag degrades instead of breaking.
    if (!c.assetBase && ownSrc) c.assetBase = ownSrc.split('seatfit-core.js')[0];
    c.kind = own(KINDS, c.kind) ? c.kind : 'seat';
    var given = c.kinds || {};
    c.kinds = {};
    Object.keys(KINDS).forEach(function (k) {
      var e = c.kinds[k] = Object.assign({ kind: k }, KINDS[k], given[k]);
      // A tag without the table describes only the page's own kind, in its top-level fields.
      if (k === c.kind && !own(given, k)) {
        ['indexUrl', 'productUrl', 'reviewsIndexUrl'].forEach(function (f) { if (c[f]) e[f] = c[f]; });
      }
      if (!e.indexUrl && c.assetBase) e.indexUrl = c.assetBase + e.prefix + 'index.json';
    });
    // The top-level URLs keep meaning the page's own product, so their readers need no kind.
    ['indexUrl', 'productUrl', 'reviewsIndexUrl'].forEach(function (f) { c[f] = c.kinds[c.kind][f]; });
    if (el) cfg = c; // cache only a real config; a call made before the tag exists retries later
    return c;
  }

  // One product kind's data files and page; a missing or unknown kind means the page's own (kind()).
  function kindConfig(kind) {
    var c = config();
    return c.kinds[own(c.kinds, kind) ? kind : c.kind];
  }

  function kind() { return config().kind; }

  // Failures and 404s are not cached: right after a theme push the CDN may briefly miss a file, and one
  // blip must not break the vehicle for the rest of the visit. A request the <head> snippet
  // (seatfit-head-prefetch) started for this URL is taken over once: on a slow phone the data then
  // arrives while the 2 MB page still parses instead of starting only after it.
  function fetchJSON(url) {
    return fetch(url).then(function (r) {
      if (r.status === 404) return null;
      if (!r.ok) throw new Error('HTTP ' + r.status + ' for ' + url);
      return r.json();
    });
  }

  // Every file asked for here is named by the index, so a 404 or network error is a blip (cold CDN edge after a
  // push, a phone switching networks): ask once more 1.5 s later before the page says it couldn't load.
  function retryOnce(url) {
    return new Promise(function (ok) { setTimeout(ok, 1500); }).then(function () { return fetchJSON(url); });
  }

  function getJSON(url) {
    if (!jsonCache[url]) {
      var early = window.__seatfitPrefetch && window.__seatfitPrefetch[url];
      if (early) delete window.__seatfitPrefetch[url];
      jsonCache[url] = (early || fetchJSON(url)).then(function (data) {
        return data == null ? retryOnce(url) : data;
      }, function () {
        return retryOnce(url);
      }).then(function (data) {
        if (data == null) delete jsonCache[url];
        return data;
      }, function (e) {
        delete jsonCache[url];
        throw e;
      });
    }
    return jsonCache[url];
  }

  function loadIndex(kind) {
    var url = kindConfig(kind).indexUrl;
    return url ? getJSON(url) : Promise.reject(new Error('[SeatFit] no fitment index URL'));
  }

  function findBy(list, slug) {
    if (!slug || !list) return null;
    for (var i = 0; i < list.length; i++) {
      if (list[i].s === slug || slugify(list[i].n) === slug) return list[i];
    }
    return null;
  }
  function loadMake(makeSlug, kind) {
    var kc = kindConfig(kind);
    return loadIndex(kc.kind).then(function (idx) {
      var mk = findBy(idx && idx.makes, slugify(makeSlug));
      if (!mk) return null; // unknown make: no request, so a typo never costs a 404 round trip
      // The index hash changes with the make file; Shopify's CDN keys its cache on ?v= only (another
      // name is ignored, so a year-old copy could be served). seatfit-head-prefetch builds this same URL.
      return getJSON(config().assetBase + kc.prefix + mk.s + '.json?v=' + encodeURIComponent(mk.h || ''));
    });
  }

  // Events go out on document: 'seatfit:open-picker' {prefill} · 'seatfit:vehicle' {ctx} ·
  // 'seatfit:selection' {ctx, row, picks, complete, kind} · 'seatfit:ready' once the rules are loaded.
  function emit(type, detail) {
    var name = String(type).indexOf('seatfit:') === 0 ? type : 'seatfit:' + type;
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
  }

  function openPicker(prefill) {
    emit('open-picker', { prefill: prefill || null });
  }

  // Theme menus and rich text often save the link with a path ("/pages/x#find-your-fit"). It
  // opens the drawer only when that path is this page; another page's link navigates normally.
  function samePage(a) {
    var before = String(a.getAttribute('href') || '').split('#')[0];
    if (before === '' || before === location.pathname) return true;
    try {
      var u = new URL(before, location.href);
      return u.origin === location.origin && u.pathname === location.pathname && !u.search;
    } catch (e) { return false; }
  }

  // One delegated listener serves every "find your fit" trigger, including ones that arrive
  // later through section re-renders. Capture phase, so a theme anchor-scroll handler that
  // stops propagation cannot swallow the click before the drawer opens.
  document.addEventListener('click', function (e) {
    var t = e.target;
    var el = t && t.closest ? t.closest('[data-seatfit-open-picker], a[href$="#find-your-fit"]') : null;
    if (!el || (!el.hasAttribute('data-seatfit-open-picker') && !samePage(el))) return;
    e.preventDefault();
    var d = el.dataset || {};
    openPicker(d.year || d.make || d.model
      ? { year: toYear(d.year), make: slugify(d.make) || null, model: slugify(d.model) || null } : null);
  }, true);

  // The files loaded after this one add resolve(), the URL params and the selection rules.
  window.SeatFit = {
    SETS: SETS, SET_VALUES: SET_VALUES, slugify: slugify, toYear: toYear, seatSlugs: seatSlugs,
    config: config, kind: kind, kindConfig: kindConfig, loadIndex: loadIndex, loadMake: loadMake, findBy: findBy,
    emit: emit, openPicker: openPicker
  };
  // Read the config while the tag is surely there: a later section re-render may replace it.
  try { config(); } catch (e) { console.warn('[SeatFit] config read failed', e); }
})();
